import React from "react";
import { navigate } from "gatsby";

import ChangeStoreDisclaimerModal from "./components/changeStoreDisclaimerModal";
// import ChangeStoreDisclaimerToast from "./components/changeStoreDisclaimerToast";

import Link from "src/components/link";
import Cart from "src/components/cart";
import ImageV2 from "src/components/image/image.v2";
import Avatar from "src/components/avatar";
import Icon, { MaterialIcon } from "src/components/icon";
import CurrencyPicker from "src/layouts/components/currencypicker";
import LanguagePicker from "src/layouts/components/languagepicker";

import { getSession, getChangeStoreDisclaimer } from "src/utils/localStorage";
import { addListener, removeListener } from "src/utils/events";
import { getLanguageField } from "src/utils/language";
import { getSlug } from "src/utils/slugs";
import { hasLoggedInCustomer } from "src/utils/user";

type ToolbarProps = {
    scrolledDown: boolean;
    toolbar: TToolbar;
    isInApp?: boolean;
    language: string;
    defaultLanguage: string;
    languages: Array<{code: string; name: string; icon: null | string}>;
    page_translations: TTranslations<{slug: string;}>;
    isScrollingUp?: boolean;

    defaultCurrency: null | string;
    currencies: Array<{id: number; code: string; name: string;}>;

    pages: {
        account_page: null | TTranslations<{slug: string}>;
        shopping_cart_page: null | TTranslations<{slug: string}>;
    }

    onLoginButtonClick: (open: boolean) => void;

    closeMenuFn: () => void;
}

export default class ToolbarCommon extends React.Component<ToolbarProps> {
    state = {
        sessionLoaded: false
    };

    componentDidMount() {
        setTimeout(() => {
            this.setState({ sessionLoaded: true });
        }, 0);

        addListener('setSession', this.handleSessionUpdate);
    }

    componentWillUnmount() {
        removeListener('setSession', this.handleSessionUpdate);
    }

    handleSessionUpdate = () => {
        // force update to re-render the component
        this.forceUpdate();
    };

    public get shadow() {
        if (this.props.toolbar.desktop_shadow) return 'shadow';
        return '';
    }

    renderLoggedInAvatar(customer: Record<string, any>) {
        return <Avatar
            size={6}
            customer={customer}
            showMeta={false}
            avatarBgClassName="bg-[color:var(--text-color)]"
            avatarTextClassName="text-[color:var(--background)]"
        />;
    }

    renderMyAccount() {
        if (!this.props.toolbar.user_profile) return null;

        let icon = <Icon icon="UserRounded" className="w-6 h-6" />;
        if (this.props.toolbar.user_profile_icon_type === "material") {
            icon = <MaterialIcon className="w-6 h-6" icon={this.props.toolbar.user_profile_material_icon as string} />;
        }
        if (this.props.toolbar.user_profile_icon_type === "custom") {
            icon = <ImageV2 asSvgIcon={true} notMapSvg src={this.props.toolbar.user_profile_custom_icon?.imageFile} className="w-6 h-6" />
        }

        // if the session is not loaded, show a opacity 50 icon without functionality
        if (!this.state.sessionLoaded) {
            return (<button key="my-account" className="text-[color:var(--text-color)] opacity-50 flex">{icon}</button>);
        }

        const session = getSession();
        const isUserLogged = hasLoggedInCustomer(session);
        const accountpage = this.props.pages?.account_page?.find(translation => translation.language === this.props.language);

        // if logged, profile link to account page
        // inntopia clients have empty customer field in session, always show the link
        if (isUserLogged) {
            return (
                <Link key="my-account" title="profile" href={accountpage?.slug} className="self-center text-[color:var(--text-color)] hover:text-[color:var(--hover-color)] flex">
                    {this.renderLoggedInAvatar(session.customer)}
                </Link>
            );
        }

        // if not logged, button to open login modal
        return (
            <button key="my-account" onClick={() => this.props.onLoginButtonClick(true)} className="text-[color:var(--text-color)] hover:text-[color:var(--hover-color)] flex">
                {icon}
            </button>
        );
    }

    renderCart() {
        if (!this.props.toolbar.shopping_cart) return null;
        const type = this.props.toolbar.shopping_cart_icon_type;
        let icon: string | undefined = undefined
        if (type === "custom") icon = this.props.toolbar.shopping_cart_custom_icon?.imageFile;
        if (type === "material") icon = this.props.toolbar.shopping_cart_material_icon;

        const cart_page = this.props.pages?.shopping_cart_page?.find(translation => translation.language === this.props.language);
        return <Link key="cart" title="" href={cart_page?.slug} className="text-[color:var(--text-color)] hover:text-[color:var(--hover-color)] flex">
            <Cart type={type} icon={icon}/>
        </Link>
    }

    renderCurrencyPicker(modalInTop: boolean = false) {
        if (!this.props.toolbar.with_currency_selector) return null;
        if (!this.props.defaultCurrency) return null;
        return <CurrencyPicker
            key="currency-picker"
            currencies={this.props.currencies}
            defaultCurrency={this.props.defaultCurrency}
            labelClassName="caption"
            itemsClassName="caption"
            modalInTop={modalInTop}
        />
    }

    renderLanguagePicker() {
        const toolbar = this.props.toolbar;
        if (!toolbar.with_language_selector) return null;
        return <LanguagePicker
            key="language-picker"
            labelClassName="caption"
            itemsClassName="caption"
            language={this.props.language}
            languages={this.props.languages}
            page_translations={this.props.page_translations}
            language_selector_on_navigation_bar={toolbar.language_selector_on_toolbar}
            language_selector_on_dropdown={toolbar.language_selector_on_dropdown}
            language_selector_icon={toolbar.language_selector_icon}
            language_flag_style={toolbar.language_flag_style}
            language_selector_dropdown_chevron_icon={toolbar.language_selector_dropdown_chevron_icon}
        />;
    }

    renderToolbarItem(item: TToolbarItem, index: number) {
        let icon = item.item.icon_type === 'personalized'
            ? <ImageV2 asSvgIcon={true} notMapSvg className="h-4 w-4 mr-2" src={item.item.personalized_icon?.imageFile} />
            : <MaterialIcon className="h-4 w-4 mr-2" icon={item.item.material_icon || ""} />;

        const label = getLanguageField(item.item.translations, this.props.language, 'label');
        const link_href = getLanguageField(item.item.link_translations, this.props.language, 'link');

        const link = item.item.link_type === 'external'
            ? link_href as string
            : getSlug(this.props.language, this.props.defaultLanguage, item.item.link_page?.translations || []);

        const closeMenuFn = this.props.closeMenuFn;

        return (
        <div key={`toolbaritem-${index}`} className="caption font-bold toolbar-item flex flex-row items-center text-[color:var(--text-color)] hover:text-[color:var(--hover-color)]" onClick={() => { closeMenuFn(); navigate(link); }}>
            { item.item.icon_type !== "none" ? icon : null }
            { label && label !== "" ? <span>{label}</span> : null}
        </div>
        );
    }

    renderChangeStoreDisclaimer() {
        const disclaimer = getChangeStoreDisclaimer();

        if (!disclaimer) return null;

        return (
            <>
                {/* <ChangeStoreDisclaimerToast data={disclaimer} /> */}
                <ChangeStoreDisclaimerModal open={true} data={disclaimer} />
            </>
        );
    }

}
